/**
 * Encrypted Events External Service
 *
 * This service sends encrypted analytics events to the external events API.
 * Events are encrypted client-side using hybrid encryption (AES-256-GCM + RSA-OAEP)
 * before transmission.
 *
 * Features:
 * - Follows mobile app pattern for API endpoint and authentication
 * - 3-retry logic with exponential backoff (1s, 2s, 4s)
 * - Payload size checking (warns if >10KB)
 * - Silent failures (logs errors but doesn't block user flow)
 * - Error tracking to Mixpanel analytics
 *
 * Endpoint: https://events-external-stage.helloheart.com/ingest
 * Auth: Bearer token (base64 encoded, matching mobile app pattern)
 */

import axios, { AxiosInstance } from 'axios';
import { env } from '@/env';
import {
  importRSAPublicKey,
  encryptEventPayload,
  isCryptoAvailable,
  isEncryptionConfigured,
} from '@/utils/encryptedEventsEncryption';
import { EncryptedEventPayload, EncryptedEventRequest } from '@/types/encryptedEvents';
import analytics from '@/services/Analytics';
import { ERROR_ENCRYPTION_FAILED, ERROR_API_CALL } from '@/consts/events';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000; // 1 second base delay
const REQUEST_TIMEOUT_MS = 10000; // 10 second timeout
const MAX_PAYLOAD_SIZE = 10240; // 10KB warning threshold

// -----------------------------------------------------------------------------
// EncryptedEventsExternalClient Class
// -----------------------------------------------------------------------------

/**
 * Client for sending encrypted events to external events API
 *
 * Singleton pattern - initialized once with RSA public key cached.
 */
class EncryptedEventsExternalClient {
  private axiosInstance: AxiosInstance;
  private publicKey: CryptoKey | null = null;
  private encryptionFeatureEnabled = false; // Whether the feature flag is on
  private encryptionReady = false; // Whether key import succeeded
  private initializationPromise: Promise<void> | null = null;

  /**
   * Create a new EncryptedEventsExternalClient instance
   *
   * @param baseURL - API hostname (e.g., "events-external-stage.helloheart.com")
   */
  constructor(baseURL: string) {
    // Setup axios instance
    this.axiosInstance = axios.create({
      baseURL: `https://${baseURL}`,
      timeout: REQUEST_TIMEOUT_MS,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${env.EVENTS_EXTERNAL_API_TOKEN}`,
      },
    });

    // Initialize encryption (async, but don't block constructor)
    this.initializationPromise = this.initializeEncryption();
  }

  /**
   * Initialize RSA public key for encryption
   *
   * This is called once during construction and caches the imported key.
   * If initialization fails, events are sent unencrypted and a Mixpanel error is tracked.
   */
  private async initializeEncryption(): Promise<void> {
    // Check feature flag — events are still sent when disabled, just not encrypted
    this.encryptionFeatureEnabled = env.EVENTS_EXTERNAL_ENCRYPTION_ENABLED === 'true';

    if (!this.encryptionFeatureEnabled) {
      console.warn('[EncryptedEventsExternal] Encryption disabled by feature flag (EVENTS_EXTERNAL_ENCRYPTION_ENABLED)');
      return;
    }

    try {
      // Check if crypto is available and configured
      if (!isCryptoAvailable()) {
        console.warn('[EncryptedEventsExternal] Web Crypto API not available (unsupported browser)');
        return;
      }

      if (!isEncryptionConfigured()) {
        console.warn('[EncryptedEventsExternal] Encryption not configured (missing public key)');
        return;
      }

      // Import RSA public key
      this.publicKey = await importRSAPublicKey(env.EVENTS_EXTERNAL_PUBLIC_KEY);
      this.encryptionReady = true;

      console.log('[EncryptedEventsExternal] Encryption initialized successfully');
    } catch (error) {
      console.error('[EncryptedEventsExternal] Failed to import public key:', error);

      // Track error to Mixpanel
      analytics.trackError(ERROR_ENCRYPTION_FAILED, {
        reason: 'key_import_failed',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }

  /**
   * Send encrypted event to external events API
   *
   * This is the main public method for tracking events. It:
   * 1. Waits for encryption initialization
   * 2. Checks payload size
   * 3. Encrypts the payload
   * 4. Sends to API with retry logic
   * 5. Handles errors silently (logs but doesn't throw)
   *
   * @param payload - Event payload to encrypt and send
   */
  async sendEvent(payload: EncryptedEventPayload): Promise<void> {
    // Wait for initialization to complete
    if (this.initializationPromise) {
      await this.initializationPromise;
      this.initializationPromise = null; // Only wait once
    }

    // Dev mode — log event and skip network call
    if (env.IS_DEV) {
      console.log('[EncryptedEventsExternal] Dev mode — skipping encrypted event:', payload.event.eventName, payload);
      return;
    }

    // Encryption enabled but failed to initialize — track the issue and fall through to send plaintext
    if (!this.encryptionReady) {
      console.warn('[EncryptedEventsExternal] Encryption not ready, sending unencrypted:', payload.event.eventName);

      analytics.trackError(ERROR_ENCRYPTION_FAILED, {
        reason: 'encryption_not_ready',
        event_name: payload.event.eventName,
      });
    }

    try {
      // Check payload size
      const payloadJson = JSON.stringify(payload);
      if (payloadJson.length > MAX_PAYLOAD_SIZE) {
        console.warn(
          `[EncryptedEventsExternal] Payload too large: ${payloadJson.length} bytes (max: ${MAX_PAYLOAD_SIZE})`,
        );

        analytics.trackError(ERROR_ENCRYPTION_FAILED, {
          reason: 'payload_too_large',
          size: payloadJson.length,
          event_name: payload.event.eventName,
        });

        return;
      }

      if (this.encryptionReady && this.publicKey) {
        // Encrypt payload and send
        const encrypted = await encryptEventPayload(payload, this.publicKey);
        const requestBody: EncryptedEventRequest = {
          encrypt: true,
          event: encrypted,
        };
        await this.sendWithRetry(requestBody, payload.event.eventName);
      } else {
        // Encryption init failed — send plaintext payload as-is
        await this.sendWithRetry(payload, payload.event.eventName);
      }

      console.log('[EncryptedEventsExternal] Event sent successfully:', payload.event.eventName);
    } catch (error) {
      console.error('[EncryptedEventsExternal] Failed to send event:', error);

      analytics.trackError(ERROR_API_CALL, {
        error: error instanceof Error ? error.message : 'Unknown error',
        event_name: payload.event.eventName,
        service: 'encrypted_events_external',
      });

      // Silent failure - don't throw, don't block user flow
    }
  }

  /**
   * Send encrypted request with exponential backoff retry logic
   *
   * Retry schedule:
   * - Attempt 1: immediate
   * - Attempt 2: wait 1s (2^0 * 1000ms)
   * - Attempt 3: wait 2s (2^1 * 1000ms)
   * - Attempt 4: wait 4s (2^2 * 1000ms)
   *
   * @param encrypted - Encrypted request body { encryptedPayload, encryptedKey }
   * @param eventName - Event name for logging
   * @param attempt - Current attempt number (1-indexed)
   * @throws Error if max retries exceeded
   */
  private async sendWithRetry(
    encrypted: EncryptedEventRequest | EncryptedEventPayload,
    eventName: string,
    attempt: number = 1,
  ): Promise<void> {
    try {
      // POST to /ingest endpoint (matches mobile app pattern)
      await this.axiosInstance.post('/ingest', encrypted);
    } catch (error) {
      // Check if we should retry
      if (attempt < MAX_RETRIES) {
        // Calculate exponential backoff delay: 1s, 2s, 4s
        const delay = RETRY_DELAY_MS * Math.pow(2, attempt - 1);

        console.warn(
          `[EncryptedEventsExternal] Request failed (attempt ${attempt}/${MAX_RETRIES}), retrying in ${delay}ms...`,
          eventName,
        );

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));

        // Retry with incremented attempt counter
        return this.sendWithRetry(encrypted, eventName, attempt + 1);
      }

      // Max retries exceeded, throw to be caught by sendEvent
      console.error(`[EncryptedEventsExternal] Max retries (${MAX_RETRIES}) exceeded for event:`, eventName);
      throw error;
    }
  }
}

// -----------------------------------------------------------------------------
// Singleton Export
// -----------------------------------------------------------------------------

/**
 * Singleton instance of EncryptedEventsExternalClient
 *
 * Initialized once with configuration from environment variables.
 * Used throughout the app for sending encrypted events.
 */
export const encryptedEventsExternalClient = new EncryptedEventsExternalClient(
  env.EVENTS_EXTERNAL_API_URL,
);
